import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  MegaphoneIcon,
  PencilIcon,
  SendIcon,
  EyeIcon,
  EyeOffIcon,
  TrashIcon,
} from 'lucide-react'

import { Button } from '@repo/ui'
import { AnnouncementKindBadge, EmptyState, StatusChip } from 'components/display'
import { ROLE_DISPLAY_NAMES } from 'constants/roles'
import { ANNOUNCEMENT_STATUSES } from 'constants/statuses'
import { announcementBodyPreview } from 'utils/announcementBody'
import { QueryErrorState } from 'components/feedback'
import { Table } from 'components/display/Table/Table'
import { ActionDropdown, ActionDropdownItem } from 'components/input/ActionDropdown'
import { Header } from 'components/navigation'
import {
  useDeleteAnnouncement,
  useInternalAnnouncements,
  useNotifyRequesters,
  useSetAnnouncementPublished,
} from 'hooks/api'
import { ROUTES } from 'routes'
import type { IAnnouncementInternal } from 'services/announcementsApi'

/**
 * `/internal/announcements` — the staff authoring surface for What's new (VRG-221).
 *
 * Its own internal sidebar surface rather than a tab inside /internal/support, by §8's
 * test: an announcement is an object staff create, edit, publish and act on across
 * instances, so it earns its own view. The support queue is where a REQUEST lives; this
 * is where the answer to many requests gets written.
 *
 * ⚠️ EXPECTED STATE AS SHIPPED: every route this page calls returns 403, because
 * migration 0057 creates `internal_announcements_manage` and grants it to NO role —
 * which roles hold it is the user's decision (§24). So the first thing this page will
 * show is `QueryErrorState`'s permission variant, naming the missing permission. That
 * is the correct rendering of the situation, and it is exactly why the error state is
 * distinguished from the empty state here: an EmptyState on a 403 would tell a staffer
 * there are no announcements when there may be several.
 */
export const InternalAnnouncementsPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { data: announcements = [], isLoading, isError, error, refetch } = useInternalAnnouncements()
  const setPublished = useSetAnnouncementPublished()
  const notify = useNotifyRequesters()
  const remove = useDeleteAnnouncement()

  const [actionError, setActionError] = useState<string | null>(null)

  // VRG-332: the editor is a PAGE now, not a dialog held open by this component's
  // state — see `InternalAnnouncementEditPage`. So creating and editing are plain
  // navigations, and a half-written draft has an address that survives a reload.
  const openCreate = () => navigate(ROUTES.INTERNAL_ANNOUNCEMENT_CREATE)

  const openEdit = (row: IAnnouncementInternal) =>
    navigate(ROUTES.INTERNAL_ANNOUNCEMENT_EDIT(row.id))

  /**
   * Every mutation funnels through here so a failure lands in ONE visible place.
   * `mutateAsync` rather than `mutate`: the point is to catch, and `mutate` swallows
   * the rejection into the mutation's own error state where this page never reads it.
   */
  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null)
    try {
      await fn()
    } catch (e) {
      setActionError((e as Error).message)
    }
  }

  const rowActions = (row: IAnnouncementInternal): ActionDropdownItem[] => {
    const published = Boolean(row.publishedAt)
    return [
      // Every row carries a leading icon (§21) — a ragged icon column reads as broken.
      {
        label: 'Edit',
        icon: <PencilIcon strokeWidth={1} />,
        onClick: () => openEdit(row),
      },
      {
        label: published ? 'Unpublish' : 'Publish',
        icon: published ? (
          <EyeOffIcon strokeWidth={1} />
        ) : (
          <EyeIcon strokeWidth={1} />
        ),
        onClick: () => run(() => setPublished.mutateAsync({ id: row.id, published: !published })),
      },
      {
        label: `Notify requesters (${row.notifyRecipientCount})`,
        icon: <SendIcon strokeWidth={1} />,
        // Standing rule (2026-08-12): all three prior conditions are RECORD STATE
        // (already sent, not published, nothing linked to notify about) — not
        // permission and not an in-flight mutation — so they hide the item
        // rather than showing it disabled with an explanatory tooltip. The
        // resolving action here (publish it, link a request) is the user's to
        // take, not something an admin grants, so there's nothing a tooltip
        // needs to point them toward.
        //
        // The tooltip stays, though, and it is NOT a disabled-reason. §46's forcing
        // function says every disabled item must explain itself; it does not say a
        // tooltip is only for disabled items. This one tells you how many people an
        // irreversible send is about to reach, which is the thing you want to know
        // BEFORE clicking, and it survived the rewrite only because it was noticed.
        tooltip: `Emails ${row.notifyRecipientCount} ${row.notifyRecipientCount === 1 ? 'person' : 'people'} who asked for this. Can only be sent once.`,
        visible: !row.notifiedAt && published && row.boardItems.length > 0,
        onClick: () => run(() => notify.mutateAsync(row.id)),
        divider: true,
      },
      {
        label: 'Delete',
        icon: <TrashIcon strokeWidth={1} />,
        // Destructive row in a transient menu → always red (§32). The record really is
        // destroyed, so Trash rather than X (§38).
        destructive: true,
        onClick: () => run(() => remove.mutateAsync(row.id)),
      },
    ]
  }

  const columns = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Announcement',
        // `type` is REQUIRED even on a renderCell column. Without it the DataGrid's filter
        // panel logs "Column type is undefined … Column was skipped" and silently drops the
        // column from filtering — caught in the browser console on this page's first render,
        // where the table mounts in its loading state before the query resolves.
        type: 'string',
        flex: 2,
        minWidth: 260,
        sortable: true,
        renderCell: ({ row }: { row: IAnnouncementInternal }) => (
          <Stack spacing={0.5} sx={{ py: 2, minWidth: 0 }}>
            <Typography variant="body3" sx={{ fontWeight: 600 }} noWrap>
              {row.title}
            </Typography>
            {/* TAGS STRIPPED, not rendered. The body is rich text now (VRG-332), and a
                one-line cell showing `<p>Export any filtered view…` would be the markup
                reading as content. Stripped rather than sanitised-and-rendered because
                this is a fixed-height row: block elements in it would break the row
                height the grid is measuring. */}
            <Typography variant="body3" color="text.secondary" noWrap>
              {announcementBodyPreview(row.body)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'kind',
        headerName: 'Kind',
        type: 'string',
        width: 150,
        sortable: true,
        // A BADGE, and specifically the SAME badge the customer's announcement card
        // renders (§36). It was the raw enum value in lowercase ("feature", "deprecation")
        // — the one place in the app where a reader saw the database's spelling of a
        // concept that has an authored label and a colour everywhere else.
        //
        // Rendering it by copying the card's tone table into this file was the obvious
        // move and is exactly the drift §36 exists to prevent, so the table was extracted
        // instead: `AnnouncementKindBadge` owns the glyphs, the tones and the
        // support-counterpart colour resolution, and both surfaces read it.
        renderCell: ({ row }: { row: IAnnouncementInternal }) => (
          <AnnouncementKindBadge kind={row.kind} />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        width: 130,
        // Draft vs published is the single most consequential fact about a row, so it
        // gets a column of its own rather than being inferred from a date cell.
        //
        // A PILL via the shared `StatusChip`, like every other list surface in the app —
        // it was a hand-rolled `<Box component="span">` with its own padding, radius and
        // brand tint, which meant an announcement's status did not look like a
        // certificate's, an order's or a support request's, and it silently opted out of
        // `statusTone`'s dark-mode handling.
        //
        // The key is DERIVED from `publishedAt` rather than read from a field, because
        // there is no status column: the nullable timestamp IS the state (see the table's
        // schema note on why it is not a separate enum).
        renderCell: ({ row }: { row: IAnnouncementInternal }) => (
          <StatusChip
            statuses={ANNOUNCEMENT_STATUSES}
            status={row.publishedAt ? 'published' : 'draft'}
            size="small"
          />
        ),
      },
      {
        field: 'requests',
        headerName: 'Closes',
        type: 'string',
        width: 150,
        renderCell: ({ row }: { row: IAnnouncementInternal }) => (
          <Typography variant="body3" color="text.secondary">
            {row.boardItems.length === 0
              ? '—'
              : `${row.boardItems.length} ${row.boardItems.length === 1 ? 'request' : 'requests'}`}
          </Typography>
        ),
      },
      {
        field: 'notified',
        headerName: 'Requesters',
        type: 'string',
        width: 150,
        // The blast radius, shown BEFORE the action rather than after: a bulk
        // notification whose reach is invisible until you click is how someone
        // messages the wrong 400 people.
        renderCell: ({ row }: { row: IAnnouncementInternal }) => (
          <Typography variant="body3" color="text.secondary">
            {row.notifiedAt
              ? 'Notified'
              : row.notifyRecipientCount === 0
                ? '—'
                : `${row.notifyRecipientCount} to notify`}
          </Typography>
        ),
      },
      {
        field: 'audience',
        headerName: 'Audience',
        type: 'string',
        width: 220,
        // ROLE NAMES, rendered as the words the roles screen uses (VRG-332). This
        // printed the raw permission slug — `manage_roles`, lowercase with an
        // underscore — which is a database identifier leaking onto a staff surface: it
        // names a permission, not an audience, and a reader has to know the catalog to
        // decode who it means.
        //
        // Truncated at two names with a "+N" rather than wrapped, so the column keeps
        // one row height. The full list is one click away in the editor, and this cell's
        // job is "is this gated, and roughly to whom".
        renderCell: ({ row }: { row: IAnnouncementInternal }) => {
          const roles = row.requiredRoleNames ?? []
          // The legacy permission still gates the announcement server-side, so a row
          // carrying one is NOT "Everyone" — saying so would be the same lie the
          // editor's legacy notice exists to prevent, in the column staff scan first.
          const legacy = row.requiredPermission
          if (roles.length === 0 && !legacy) {
            return (
              <Typography variant="body3" color="text.secondary" noWrap>
                Everyone
              </Typography>
            )
          }
          const shown = roles.slice(0, 2).map(name => ROLE_DISPLAY_NAMES[name] ?? name)
          const parts = [...shown]
          if (roles.length > shown.length) parts.push(`+${roles.length - shown.length}`)
          if (legacy) parts.push(roles.length > 0 ? '+ permission' : 'By permission')
          return (
            <Typography variant="body3" color="text.secondary" noWrap>
              {parts.join(', ')}
            </Typography>
          )
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 64,
        sortable: false,
        // No `type` here, deliberately: the actions column is excluded from filtering and
        // its column menu, which is what keeps it out of the warning above.
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: 'center',
        pinned: 'right',
        renderCell: ({ row }: { row: IAnnouncementInternal }) => (
          <ActionDropdown items={rowActions(row)} />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, announcements],
  )

  return (
    <Box>
      <Header
        title="What's new"
        breadcrumbs={false}
        total={announcements.length}
        rightComponents={
          // One primary, rightmost (§26/§33).
          //
          // Suppressed entirely when the list query failed. Caught in the browser: with
          // the permission ungranted the page correctly showed its permission error, but
          // this button stayed live — so the one affordance on screen was an action
          // guaranteed to 403. §27 wants an action that cannot succeed to be visibly
          // unavailable, and here the error state directly above already says why, so
          // hiding beats a disabled button repeating the same tooltip.
          isError ? null : (
            <Button variant="primary" onClick={openCreate}>
              New announcement
            </Button>
          )
        }
      />

      {actionError && (
        <Typography
          variant="body3"
          sx={{ color: theme.palette.error.main, lineHeight: 1.5, mb: 3 }}
        >
          {actionError}
        </Typography>
      )}

      {isError ? (
        <QueryErrorState
          error={error}
          subject="announcements"
          // Names the SPECIFIC permission, so a 403 is actionable rather than a dead
          // end — this is the state the page ships in until the role grant is decided.
          permissionLabel="Manage what's new"
          onRetry={() => refetch()}
        />
      ) : (
        <Table
          // The shared Table renders a column-aware skeleton whenever `loading` is
          // true (§19), so no separate spinner and no hand-rolled skeleton here.
          loading={isLoading}
          columns={columns}
          rows={announcements}
          getRowId={(row: IAnnouncementInternal) => row.id}
          // The table IS this page's content, so it fills to a bottom gap matching the
          // 24px L/R gutter (§22).
          fillBottom
          showSelection={false}
          alwaysShowRowSelection={false}
          emptyState={
            <EmptyState
              icon={<MegaphoneIcon strokeWidth={1} />}
              title="No announcements yet"
              text="Write one when something ships. Link the feature requests it closes, publish it, then notify everyone who asked for it."
              buttons={
                <Button variant="primary" onClick={openCreate}>
                  New announcement
                </Button>
              }
            />
          }
        />
      )}
    </Box>
  )
}
